import React, { useEffect, useState, useCallback } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { AlertTriangle, ArrowRight, XCircle, Phone } from 'lucide-react';
import { ETAPE, statusLabel, CONVERT_LABEL } from './crmLabels';
import RenuntaDialog from './RenuntaDialog';
import { toast } from 'sonner';

export default function PipelineView({ api, settings, onOpenClient }) {
  const [cols, setCols] = useState({ 1: [], 2: [], 3: [], 4: [] });
  const [loading, setLoading] = useState(true);
  const [renunta, setRenunta] = useState(null); // {solicitareId, etapa}

  const load = useCallback(async () => {
    setLoading(true);
    let ok = false;
    for (let i = 0; i < 4 && !ok; i++) {
      try {
        const data = await api.get('/pipeline');
        setCols(data.pipeline);
        ok = true;
      } catch (e) {
        await new Promise((r) => setTimeout(r, 800));
      }
    }
    if (!ok) toast.error('Eroare la încărcarea pipeline-ului');
    setLoading(false);
  }, [api]);

  useEffect(() => { load(); }, [load]);

  const doConvert = async (card) => {
    try {
      await api.post(`/solicitari/${card.solicitareId}/convert`);
      toast.success('Client avansat în etapa următoare');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Conversia nu este permisă');
    }
  };

  const doRenunta = async (motiv) => {
    await api.post(`/solicitari/${renunta.solicitareId}/renunta`, { motiv });
    toast.success('Client arhivat (renunțare)');
    load();
  };

  const motiveFor = (etapa) => (settings?.motive?.[String(etapa)]) || [];

  return (
    <div data-testid="crm-pipeline">
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
        {[1, 2, 3, 4].map((e) => (
          <div key={e} className="bg-gray-50 rounded-xl p-3 min-h-[200px]" data-testid={`crm-col-${e}`}>
            <div className="flex items-center justify-between mb-3 px-1">
              <h3 className="font-semibold text-sm text-gray-800">{ETAPE[e]}</h3>
              <Badge variant="secondary">{cols[e]?.length || 0}</Badge>
            </div>
            <div className="space-y-3">
              {(cols[e] || []).map((card) => (
                <Card
                  key={card.solicitareId}
                  className={`border ${card.alerta ? 'border-red-400 bg-red-50' : 'border-gray-200'} cursor-pointer hover:shadow-md transition-shadow`}
                  data-testid={`crm-card-${card.clientId}`}
                  onClick={() => onOpenClient(card.clientId)}
                >
                  <CardContent className="p-3 space-y-2">
                    <div className="flex items-start justify-between">
                      <div className="font-medium text-sm">{card.nume}</div>
                      {card.tipIntrare === 'portofoliu' && (
                        <Badge className="bg-purple-100 text-purple-700 text-[10px]">portofoliu</Badge>
                      )}
                    </div>
                    <div className="text-xs text-gray-500 flex items-center gap-1">
                      <Phone className="h-3 w-3" /> {card.telefon || '—'}
                    </div>
                    <Badge variant="outline" className="text-[11px]">{statusLabel(card.status)}</Badge>
                    {e === 3 && card.dosare && (
                      <div className="text-[11px] text-gray-600" data-testid={`crm-card-dosare-${card.clientId}`}>
                        {card.dosare.total > 0
                          ? `Dosare: ${card.dosare.total} (${card.dosare.banci.filter(Boolean).join(', ')})`
                          : 'Niciun dosar încă'}
                      </div>
                    )}
                    {e !== 4 && (
                      card.urmatorulPas?.text ? (
                        <div className={`text-xs ${card.alerta ? 'text-red-600 font-medium' : 'text-gray-600'}`}>
                          Următorul pas: {card.urmatorulPas.text} {card.urmatorulPas.data ? `(${card.urmatorulPas.data})` : ''}
                        </div>
                      ) : (
                        <div className="text-xs text-red-600 font-medium flex items-center gap-1">
                          <AlertTriangle className="h-3 w-3" /> Fără următorul pas
                        </div>
                      )
                    )}
                    <div className="flex gap-2 pt-1" onClick={(ev) => ev.stopPropagation()}>
                      {(e === 1 || e === 2) && (
                        <Button
                          size="sm"
                          className="h-7 text-[11px] px-2 bg-emerald-600 hover:bg-emerald-700"
                          onClick={() => doConvert(card)}
                          data-testid={`crm-convert-${card.clientId}`}
                        >
                          <ArrowRight className="h-3 w-3 mr-1" />Conversie
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-7 text-[11px] px-2 text-red-600 border-red-200"
                        onClick={() => setRenunta({ solicitareId: card.solicitareId, etapa: e })}
                        data-testid={`crm-renunta-${card.clientId}`}
                      >
                        <XCircle className="h-3 w-3 mr-1" />Renunță
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
              {(cols[e] || []).length === 0 && !loading && (
                <p className="text-xs text-gray-400 px-1">Niciun client</p>
              )}
            </div>
          </div>
        ))}
      </div>

      <RenuntaDialog
        open={!!renunta}
        onClose={() => setRenunta(null)}
        motive={renunta ? motiveFor(renunta.etapa) : []}
        onConfirm={doRenunta}
      />
    </div>
  );
}
